import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { UserService } from '../../../../../services/user.service';
import { StudentService } from '../../../../../services/student.service';
import { ProfileService } from '../../../../../services/profile.service';

/**
 * Handles account deletion. Presents a two-step confirm/cancel UI,
 * then sequentially deletes the profile, student, and nulls the user's
 * role before redirecting to the home route.
 */
@Component({
  selector: 'app-deletion',
  imports: [CommonModule, MatCardModule, MatButtonModule, MatDividerModule],
  templateUrl: './deletion.html',
  styleUrl: './deletion.css',
})
export class Deletion {
  /** Whether a deletion request is currently in flight. */
  isDeleting = false;

  /** Whether the confirmation UI is visible. */
  showConfirm = false;

  /** Error message displayed if any step in the deletion sequence fails. */
  error = '';

  constructor(
    /** Router used to navigate away after successful deletion. */
    private router: Router,
    /** Service for fetching and updating the current user record. */
    private userService: UserService,
    /** Service for fetching and deleting the student record. */
    private studentService: StudentService,
    /** Service for deleting the student's profile record. */
    private profileService: ProfileService,
  ) {}

  /**
   * Shows the confirmation UI.
   * @returns {void}
   */
  confirmDelete(): void {
    this.showConfirm = true;
  }

  /**
   * Hides the confirmation UI without taking any action.
   * @returns {void}
   */
  cancelDelete(): void {
    this.showConfirm = false;
  }

  /**
   * Executes the deletion sequence: profile → student → user role nulled.
   * Each step depends on the previous succeeding. On completion (or a role
   * update failure, which is treated as non-fatal), navigates to '/'.
   * @returns {void}
   */
  deleteProfile(): void {
    const user = this.userService.currentUser;
    if (!user) return;

    this.isDeleting = true;
    this.error = '';

    this.studentService.getStudentByUserId(user.id).subscribe({
      next: (student) => {
        this.profileService.deleteMyProfile().subscribe({
          next: () => {
            this.studentService.deleteStudent(student.id).subscribe({
              next: () => {
                this.userService.updateUser(user.id, { role: null } as any).subscribe({
                  next: () => {
                    this.isDeleting = false;
                    this.router.navigate(['/']);
                  },
                  error: () => {
                    // Role update failure is non-fatal — navigate away regardless
                    this.isDeleting = false;
                    this.router.navigate(['/']);
                  },
                });
              },
              error: (err) => {
                console.error(err);
                this.error = 'Something went wrong.';
                this.isDeleting = false;
              },
            });
          },
          error: (err) => {
            console.error(err);
            this.error = 'Something went wrong.';
            this.isDeleting = false;
          },
        });
      },
      error: (err) => {
        console.error(err);
        this.isDeleting = false;
      },
    });
  }
}
